import React, {useEffect, useRef, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {useTranslate} from "../../../system/useTranslate";
import {Button} from "primereact/button";
import Icon from "../../../system/components/icon";
import {RadioButton} from "primereact/radiobutton";
import {AutoComplete} from "primereact/autocomplete";
import {ProgressBar} from "primereact/progressbar";
import {getUploadErrorMessage} from "../../components/upload-dialog";
import {fileThumbnail, getFileSize} from "../../../system/utils";
import api from "../../../system/api";
import {useSelector} from "react-redux";

function UploadDialog({visible, onHide, onSubmit, payload, setFile}) {
    const translate = useTranslate();
    const [linkTo, setLinkTo] = useState("task");
    const [filteredTasks, setFilteredTasks] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState(null);
    const hiddenFileInput = useRef();
    const {workspaceId} = useSelector((state) => state.app);
    // the upload goes through the workspace's secure media, like every other upload (it was sent to the raw «/upload»
    // and refused); while it runs the progress shows and the button cannot be pressed again (boss's bug report 2026-10-28)
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState("");
    const [taskMissing, setTaskMissing] = useState(false);
    const uploadingRef = useRef(false);
    const uploading = progress !== null;

    useEffect(() => {
        if (!visible) {
            setProgress(null);
            setError("");
            setTaskMissing(false);
        }
    }, [visible]);


    const search = (event) => {
        // Timeout to emulate a network connection
        setTimeout(() => {
            let _filteredTasks;

            if (!event.query.trim().length) {
                _filteredTasks = [...payload?.tasks];
            }
            else {
                _filteredTasks = payload?.tasks.filter((task) => {
                    return task.title.toLowerCase().startsWith(event.query.toLowerCase());
                });
            }
            setFilteredTasks(_filteredTasks);
        }, 250);
    }

    const uploadHandler= async (e)=>{
        const file = e.files[0];
        if (!file || uploadingRef.current) return;
        if (linkTo==="task"&&(selectedTasks?.length===0||!selectedTasks)) {
            setTaskMissing(true);
            return;
        }
        const data = new FormData();
        data.append("files", file);
        data.append("fileInfo", JSON.stringify({name: file.name}));
        data.append("entityType", "project");
        if (payload?.projectDocumentId) data.append("entityDocumentId", payload.projectDocumentId);
        data.append("purpose", "attachment");

        uploadingRef.current = true;
        setError("");
        setTaskMissing(false);
        setProgress(0);
        try {
            const res = await api.post("/workspace-media/upload", data, {
                headers: {"Content-Type": "multipart/form-data", workspace: workspaceId},
                suppressGlobalErrorHandler: true,
                onUploadProgress: (event) => {
                    const ratio = event.total ? event.loaded / event.total : event.progress;
                    if (Number.isFinite(ratio)) setProgress((current) => Math.max(current || 0, Math.min(99, Math.round(ratio * 100))));
                },
            });
            const body = res?.data?.data ?? res?.data;
            const uploadedData = Array.isArray(body) ? body : [body].filter(Boolean);
            setProgress(100);
            await onSubmit?.({uploadedData, linkTo, selectedTasks});
            setFile?.(undefined);
            setSelectedTasks(null);
            onHide?.();
            return uploadedData;
        } catch (err) {
            const key = err?.response?.data?.error?.message || err?.normalizedError?.message;
            const translated = key ? translate(key) : "";
            setError(key && translated !== key ? translated : getUploadErrorMessage(err) || "بارگذاری انجام نشد. دوباره تلاش کن.");
        } finally {
            uploadingRef.current = false;
            setProgress(null);
        }
    }

    return (
        <Dialog headerClassName="border-bottom-1 border-200" pt={{content:()=>({className:"p-3"}),header:()=>({className:"border-round-top-2xl p-3 text-sm"}), footer:()=>({className:"p-0 border-round-bottom-2xl"}), root:()=>({className:"border-round-2xl"})}} header={translate("add-files-to-project")} footer={<Footer uploading={uploading} disabled={!payload?.file} onHide={onHide} onSubmit={()=>{uploadHandler({files:[payload?.file]})}} />} className="w-11 md:w-6 xl:w-4 rtl" visible={visible} closable={!uploading} onHide={()=>{ if (!uploading) onHide?.(); }}>
            <div className="flex flex-column gap-1">
                <span className="text-xs font-bold">{translate("choose-file")}</span>
                {payload?.file?(<ChooseFile disabled={uploading} onFileDeleted={()=>{setFile(undefined)}} file={payload?.file}/>):(
                    <div className="w-full">
                        <Button className="text-xs w-full p-button-outlined" label={translate("choose-file")} onClick={()=>{hiddenFileInput.current.click()}}/>
                        <input ref={hiddenFileInput} className="p-hidden" type="file" onChange={(e)=>{const _file = e.target.files[0]; setFile(_file)}}/>
                    </div>

                )}
                {uploading && <div className="flex flex-column gap-1 mt-2" aria-live="polite">
                    <div className="flex justify-content-between text-xs text-600"><span>در حال بارگذاری…</span><span className="ltr">{progress}%</span></div>
                    <ProgressBar value={progress} showValue={false} style={{height: ".4rem"}}/>
                </div>}
                {error && <small className="text-xs text-red-500 mt-2">{error}</small>}
                <span className="text-xs font-bold mt-3">{translate("choose-file-destination")}</span>
                <div className={`flex flex-column gap-2 ${uploading ? "p-disabled" : ""}`}>
                    <div className="flex gap-1">
                        <RadioButton name="link-to" checked={linkTo==="task"} onChange={(e)=>{setLinkTo("task")}}/>
                        <div className="flex flex-column w-full text-xs gap-1 mt-1">
                            <label className="font-bold" htmlFor="link-to">{translate("link-to-tasks")}</label>
                            <span className="text-2xs text-400">{translate("link-to-tasks-description")}</span>
                            <div className={`w-full border-round surface-50 border-1 border-200 p-2 flex flex-column gap-1 ${linkTo!=="task"&&"p-disabled"}`}>
                                <span className="text-2xs text-400 ">{translate("search-task")}</span>
                                <AutoComplete  panelClassName="rtl" inputClassName="shabnam-font text-xs" multiple field="title" suggestions={filteredTasks} completeMethod={search} value={selectedTasks} onChange={(e) => { setSelectedTasks(e.value); setTaskMissing(false); }} className="w-full rtl" pt={{item:()=>({className:"text-xs p-2"}),token:()=>({className:"text-xs gap-2 pl-0 m-0"}),loadingIcon:()=>({style:{left:"0.75rem"}}), container:()=>({className:"w-full px-2 gap-2"})}}/>
                            </div>
                            {taskMissing && <small className="text-2xs text-red-500">حداقل یک کار را انتخاب کن یا «پیوست به پروژه» را بزن.</small>}
                        </div>
                    </div>
                    <div className="flex gap-1">
                        <RadioButton name="link-to" checked={linkTo==="project"} onChange={(e)=>{setLinkTo("project")}}/>
                        <div className="flex flex-column w-full text-xs gap-1 mt-1">
                            <label className="font-bold" htmlFor="link-to">{translate("link-to-projects")}</label>
                            <span className="text-2xs text-400">{translate("link-to-projects-description")}</span>
                        </div>
                    </div>

                </div>

            </div>
        </Dialog>
    );
}

function ChooseFile({file, disabled, onFileDeleted}) {
    const [preview, setPreview] = useState(null);
    const [fileInfo, setFileInfo] = useState(null);
    const translate = useTranslate();

    const getPreview = (_file) => {

        if (_file) {
            const reader = new FileReader(); // ایجاد یک FileReader جدید برای هر فایل

            reader.onload = (readEvent) => {
                const dataURL = readEvent.target.result;
                setPreview(dataURL); // ذخیره Data URL در state
            };

            reader.readAsDataURL(_file); // شروع خواندن فایل به عنوان Data URL
        }
    };

    useEffect(()=>{
        if (file){
            if (file?.type.split("/")[0]==="image"){
                getPreview(file);
            }
            setFileInfo({...getFileSize(file?.size), name:file?.name, type:file?.type})
        }
        else {
            setPreview(null)
        }
    },[file])

    return(
        <div className="border-round surface-50 p-2 border-1 border-200 flex align-items-center justify-content-between">
            <div className="flex align-items-center gap-2">
                {fileInfo&&fileThumbnail(fileInfo?.name, preview, preview?"10rem":"4rem", false)}
                <div className="flex flex-column">
                    <span className="text-xs font-bold overflow-hidden text-overflow-ellipsis">{fileInfo?.name}</span>
                    <span className="text-xs text-300">{`${fileInfo?.size} ${translate(fileInfo?.unit)}`}</span>
                </div>
            </div>
            <Button disabled={disabled} onClick={()=>{onFileDeleted?.(file)}} className="p-button-rounded p-button-text p-button-danger shadow-none border-none" icon={<Icon icon="mi mi:delete_forever"/>}/>
        </div>
    )
}

function Footer({onHide, onSubmit, uploading, disabled}) {
    const translate = useTranslate();
    return(
        <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
            <Button icon={<Icon icon="mi mi:cloud_upload" className="text-lg"/>} className="text-xs flex gap-2 border-round-lg p-2" label={uploading ? "در حال بارگذاری…" : translate("upload-save")} loading={uploading} disabled={uploading || disabled} onClick={onSubmit}/>
            <Button className="text-xs p-button-secondary p-button-outlined border-round-lg p-2" label={translate("cancel")} disabled={uploading} onClick={()=>{onHide?.()}}/>
        </div>
    )
}

export default UploadDialog;
